// The Test Bed's accuracy box (80.8): the site's accuracy across its question
// sets, this run against the last, what changed in between, and the details
// per set behind a chevron. It stands in the right-hand column on the one
// info pane every surface uses. The figures come from GET /api/v1/eval/scorecard;
// the key is the grade, so the box fills in from a run with nobody grading.
import { useCallback, useEffect, useState } from "react";
import { useApiSession } from "@/lib/useApiSession";
import {
  downloadScorecardReport,
  getScorecard,
  type BandName,
  type ScoreBank,
  type ScoreLever,
  type Scorecard as ScorecardData,
} from "@/lib/api";
import { useActiveDatabase } from "@/lib/useDatabases";
import { bandOf, deltaWords, percent } from "@/lib/scorecard";
import { buttonVariants } from "@/components/ui/Button";
import { Message } from "@/components/ui/Message";
import { Pane } from "@/components/ui/Pane";
import { cn } from "@/lib/utils";

const BAND_TILE: Record<BandName, string> = {
  red: "bg-tres-band-red",
  pink: "bg-tres-band-pink",
  yellow: "bg-tres-band-yellow",
  amber: "bg-tres-band-amber",
  "pale-green": "bg-tres-band-pale-green",
  green: "bg-tres-band-green",
};

const BAND_SOFT: Record<BandName, string> = {
  red: "bg-tres-band-red-soft",
  pink: "bg-tres-band-pink-soft",
  yellow: "bg-tres-band-yellow-soft",
  amber: "bg-tres-band-amber-soft",
  "pale-green": "bg-tres-band-pale-green-soft",
  green: "bg-tres-band-green-soft",
};

/** The kinds of change shown before the rest are left to the full report. */
const CHANGES_SHOWN = 6;

/** What changed since the last run, one line per kind of change (the server
 *  groups them), and once, not on every line, that a change recorded without
 *  a database may belong to a different one. */
function ChangeList({ levers, testId }: { levers: ScoreLever[]; testId?: string }) {
  const shown = levers.slice(0, CHANGES_SHOWN);
  const more = levers.length - shown.length;
  return (
    <div className="mt-2 text-sm" data-testid={testId}>
      <div className="font-medium">Since the last run</div>
      <ul className="mt-0.5 list-disc space-y-0.5 pl-5 text-muted-foreground">
        {shown.map((l) => (
          <li key={l.what}>{l.what}</li>
        ))}
        {more > 0 && (
          <li>
            {more} more {more === 1 ? "kind" : "kinds"} of change, in the full report
          </li>
        )}
      </ul>
      {levers.some((l) => l.site_wide) && (
        <p className="mt-1 text-xs text-muted-foreground">
          Some of these were recorded without a database, so they may belong to a different one on this site.
        </p>
      )}
    </div>
  );
}

function keyedWords(t: { asked: number; keyed: number; not_keyed: number; errored: number }): string {
  const parts = [t.not_keyed > 0 ? `${t.not_keyed} not keyed` : "every answer keyed"];
  if (t.errored > 0) parts.push(`${t.errored} not answered`);
  return parts.join(" · ");
}

function DeltaTile({ delta, hasPrior }: { delta: number | null; hasPrior: boolean }) {
  const arrow = delta === null || delta === 0 ? "" : delta > 0 ? "↑ " : "↓ ";
  return (
    <div
      className={cn(
        "flex flex-col items-center justify-center rounded-md px-3 py-3 text-center",
        delta === null ? "bg-muted text-muted-foreground" : "bg-tres-action text-primary-foreground",
      )}
      data-testid="scorecard-delta"
    >
      <div className="text-2xl font-semibold leading-tight">
        {arrow}
        {delta !== null
          ? `${Math.floor(Math.abs(delta) * 100 + 0.5)}%`
          : hasPrior
            ? "No comparison"
            : "First run"}
      </div>
      <div className="mt-1 text-xs">
        {delta !== null
          ? "since the last run"
          : hasPrior
            ? "needs keyed answers in this run and the last"
            : "nothing earlier to compare"}
      </div>
    </div>
  );
}

function BankDetails({ bank }: { bank: ScoreBank }) {
  return (
    <div className="rounded-md border border-border p-3" data-testid="scorecard-bank">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div className="text-sm font-medium">
          {bank.name}{" "}
          <span className="font-normal text-muted-foreground">
            ({bank.asked} {bank.asked === 1 ? "question" : "questions"})
          </span>
        </div>
        <div className="text-sm">
          <span
            className={cn(
              "rounded px-1.5 py-0.5 font-medium text-primary-foreground",
              bank.band ? BAND_TILE[bank.band] : "bg-muted text-muted-foreground",
            )}
          >
            {percent(bank.rate)}
          </span>{" "}
          <span className="text-muted-foreground">{deltaWords(bank.delta, bank.prior_run_id !== null)}</span>
        </div>
      </div>
      <div className="mt-1 text-xs text-muted-foreground">
        {bank.model}
        {bank.build ? ` · build ${bank.build}` : " · build not recorded"}
        {bank.prior_model ? ` · the last run used ${bank.prior_model}` : ""}
        {bank.without_ontology !== null ? ` · without the ontology ${percent(bank.without_ontology)}` : ""}
        {` · ${keyedWords(bank)}`}
      </div>
      {bank.levers.length > 0 && <ChangeList levers={bank.levers} />}
      {bank.causes.length > 0 && (
        <div className="mt-2 text-xs" data-testid="scorecard-causes">
          <span className="font-medium">Why the answers missed:</span>{" "}
          <span className="text-muted-foreground">
            {bank.causes.map((c) => `${c.label} (${c.count})`).join(", ")}
          </span>
        </div>
      )}
      <ul className="mt-2 space-y-1 text-sm">
        {bank.families.map((f) => (
          <li
            key={f.family}
            className={cn("rounded px-2 py-1", f.rate !== null && BAND_SOFT[bandOf(f.rate)])}
          >
            <span className="font-medium">{f.family}</span>
            <span className="text-muted-foreground"> ({f.asked}): </span>
            <span className="font-medium">{percent(f.rate)}</span>
            <span className="text-muted-foreground">, {deltaWords(f.delta, bank.prior_run_id !== null)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** The box, from data already in hand. */
export function ScorecardView({
  data,
  onDownload,
  downloading,
  downloadMessage,
}: {
  data: ScorecardData;
  onDownload?: () => void;
  downloading?: boolean;
  downloadMessage?: string | null;
}) {
  const [open, setOpen] = useState(false);
  const { overall } = data;
  const noSets = data.banks.length === 0 && data.unrun_sets.length === 0;
  const noRuns = data.banks.length === 0 && data.unrun_sets.length > 0;

  return (
    <Pane title="LLM Accuracy" subtitle={`Across all question sets on ${data.database}`} className="self-start">
      <div data-testid="scorecard">
        {noSets ? (
          <p className="text-sm text-muted-foreground">
            No question set yet. Create one under Run a question set, give each question a key,
            and run it: the box fills in from the first run, with nobody grading.
          </p>
        ) : noRuns ? (
          <p className="text-sm text-muted-foreground">
            No run yet. Run a question set and the box fills in.
          </p>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-3">
              <div
                className={cn(
                  "flex flex-col items-center justify-center rounded-md px-3 py-3 text-center text-primary-foreground",
                  overall.band ? BAND_TILE[overall.band] : "bg-muted text-muted-foreground",
                )}
                data-testid="scorecard-rate"
              >
                <div className="text-4xl font-semibold leading-tight">
                  {overall.keyed === 0 ? "No keys" : percent(overall.rate)}
                </div>
                <div className="mt-1 text-xs">
                  {overall.keyed} of {overall.asked} {overall.asked === 1 ? "answer" : "answers"} keyed
                </div>
              </div>
              <DeltaTile delta={overall.delta} hasPrior={overall.banks_with_prior > 0} />
            </div>
            {overall.keyed === 0 && overall.asked > 0 && (
              <Message kind="guidance" className="mt-2" data-testid="scorecard-no-keys">
                None of these answers has a key, so there is no accuracy to show. Give each question its key in its
                set, then run the set again.
              </Message>
            )}
            {overall.keyed > 0 && (
              <div className="mt-2 text-xs text-muted-foreground">
                {keyedWords(overall)}
                {overall.prior_rate !== null ? ` · last run ${percent(overall.prior_rate)}` : ""}
              </div>
            )}
            {data.levers.length > 0 && <ChangeList levers={data.levers} testId="scorecard-levers" />}
            {overall.new_sets.length > 0 && (
              <div className="mt-1 text-xs text-muted-foreground">
                New {overall.new_sets.length === 1 ? "set" : "sets"} with no earlier run:{" "}
                {overall.new_sets.join(", ")}
              </div>
            )}
            {data.unrun_sets.length > 0 && (
              <div className="mt-1 text-xs text-muted-foreground">
                Not yet run: {data.unrun_sets.join(", ")}
              </div>
            )}

            <button
              type="button"
              onClick={() => setOpen((o) => !o)}
              aria-expanded={open}
              className={cn(buttonVariants({ variant: "outline", size: "sm" }), "mt-3 w-full justify-between")}
            >
              <span>Details per set</span>
              <span aria-hidden="true">{open ? "▾" : "▸"}</span>
            </button>
            {open && (
              <div className="mt-2 space-y-2">
                {data.banks.map((b) => (
                  <BankDetails key={b.bank_id} bank={b} />
                ))}
              </div>
            )}
          </>
        )}

        {onDownload && (
          <div className="mt-3 flex flex-col items-start gap-1">
            <button
              type="button"
              onClick={onDownload}
              disabled={downloading || data.banks.length === 0}
              className={buttonVariants({ variant: "link", size: "sm" })}
            >
              {downloading ? "Preparing the report…" : "Download the full report"}
            </button>
            {downloadMessage && <span className="text-xs text-muted-foreground">{downloadMessage}</span>}
          </div>
        )}
      </div>
    </Pane>
  );
}

/** The box with its data read from the site, refreshed when the database
 *  changes or when a run completes (`refreshKey`). */
export function Scorecard({ refreshKey }: { refreshKey?: unknown }) {
  // A guest on a site that serves guests reads the box too; the session
  // hook says when sign-in has settled, so the box never waits on a token
  // that is not coming.
  const { token, ready } = useApiSession();
  const { active } = useActiveDatabase();
  const [data, setData] = useState<ScorecardData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [downloadMessage, setDownloadMessage] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!ready) return;
    const r = await getScorecard(token);
    if (r.ok) {
      setData(r.data);
      setError(null);
    } else {
      setError(r.message);
    }
  }, [token, ready]);

  useEffect(() => {
    void load();
  }, [load, active, refreshKey]);

  const onDownload = async () => {
    setDownloading(true);
    setDownloadMessage(null);
    const r = await downloadScorecardReport(token);
    setDownloading(false);
    setDownloadMessage(r.ok ? `Saved ${r.filename}` : `The report could not be downloaded: ${r.message}`);
  };

  if (error) {
    return (
      <Message kind="warning" className="self-start">
        The accuracy box could not be read: {error}
      </Message>
    );
  }
  if (!data) {
    return (
      <Pane title="LLM Accuracy" subtitle="Across all question sets" loading className="self-start">
        <span />
      </Pane>
    );
  }
  return (
    <ScorecardView
      data={data}
      onDownload={() => void onDownload()}
      downloading={downloading}
      downloadMessage={downloadMessage}
    />
  );
}
